import {
  LightModeSemanticColors,
  fontSizes,
} from '@enechain/ecloud-designsystem'

import { isEnterKey } from '~/libs/keyboard'

// `pages/BalanceDemand` / `pages/BalanceGeneration` の FilterCard が系列表示切替に使う
// カスタム on/off スイッチ (= design HTML 準拠のピル型トグル)。 BalanceDemand 側のみ
// isDisabled (分析補助/詳細セクションの親チェックボイス連動) を使う。

type Props = {
  label: string
  isOn: boolean
  /** 親チェックボックス等での無効化。 省略時 false (= BalanceGeneration 側は常に有効)。 */
  isDisabled?: boolean
  /** ON 時のトラック色 (design .switch-item[data-color] = チャート凡例色に揃える)。
   *  省略時は accent-primary (= data-color なしの switch と同じ)。 */
  onColor?: string
  onToggle: () => void
}

const switchStyle = (isDisabled: boolean): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  // design balance.css `.switch-item` = `--font-size-md` (14px) / weight 400 (2026-08-06 実測)。
  // 素の DOM style なので ecloud fontSizes の解決値を直書きする (= token 文字列は chakra prop 専用)。
  // weight は指定せず body の 400 を継承させる (design も weight 指定なし)。
  fontSize: fontSizes.md.value,
  cursor: isDisabled ? 'default' : 'pointer',
  whiteSpace: 'nowrap',
  userSelect: 'none',
  opacity: isDisabled ? 0.4 : 1,
})

const toggleKnobStyle = (
  isOn: boolean,
  onColor: string,
): React.CSSProperties => ({
  display: 'inline-block',
  width: '26px',
  height: '16px',
  borderRadius: '9999px',
  // OFF トラックは DS Switch の base recipe と同じ `Surface.quinary` (#d9d9d9)。
  // 実装は 1 段明るい quaternary (#e0e0e0) を当てていたが、 下の #6422 の実測値
  // (太陽光 1.05 / バイオマス 1.66 / 発電実績 1.89 / accent 8.17 / 白ノブ 1.41) は
  // すべて quinary に対する比で、 コメントだけが正しく実装がずれていた。
  background: isOn ? onColor : LightModeSemanticColors.Surface.quinary.value,
  position: 'relative',
  flexShrink: 0,
  transition: 'background 0.2s',
})

/**
 * ノブ (つまみ)。 `hasSeriesColor` の時だけ**影を濃くする**。
 *
 * 系列色を ON トラックに使うと **ON と OFF を見分けられない**組み合わせが出る
 * (issue #6422、 2026-08-25 実測。 ON トラック vs OFF トラック `Surface.quaternary` の
 * コントラスト): 太陽光 `#ffdd29` = 1.05:1 / バイオマス `#bb97ff` = 1.66:1 /
 * 発電実績 `#00b0b2` = 1.89:1。 既定の accent (`#0c00c5`) は 8.17:1 なので
 * 従来は問題化しなかった。
 *
 * 輝度差が無い以上、 残る状態の手掛かりは**ノブの位置**だが、 ノブは白で
 * トラックとの比が 1.34〜1.41:1 しかなく位置が読めない。 影で輪郭を立てて読めるようにする。
 * 系列色そのものは動かさない — チャートの塗り色と同じ定数 (`chartPalette.ts`) を
 * 引いており、 暗くするとチャート側の識別性が落ちるため。
 *
 * ## なぜ枠線ではなく影か
 *
 * 当初は `1px solid Object.highEmphasis` の枠線で解いたが、 **DS の switch と別部品に
 * 見える**ため取りやめた (2026-08-26 デザイナー判断)。 DS Switch の thumb は
 * 「白 + `boxShadow`」 で、 枠線を持たない (chakra `switch.js` の solid variant =
 * `bg: white` / `boxShadow: "sm"`)。 影という**同じ手段**のまま濃さだけ上げれば、
 * DS のニュアンスを残したまま輪郭を立てられる。
 *
 * 濃さは上げざるを得ない。 eclair の shadow トークンは最も濃い `shadBase` でも
 * `rgba(0,0,0,0.07)` で、 太陽光の上ではノブが完全に溶ける (実測)。 そのため
 * トークンではなく値を直接書く。 **形は DS と同じ drop shadow で、 リング
 * (`0 0 0 Npx`) は使わない** — リングは枠線に見えてしまい、 避けたかった見え方に
 * 戻るため (候補 A〜F を系列色 7 種に並べて比較したうえでの選択)。
 *
 * [DS差異] 系列色を track に載せるのが DS 想定外の用法なので、 **その用法に限って**
 * 影を濃くする (data-color 相当 = `onColor` を明示指定された時だけ)。 既定の accent
 * では DS どおり影なし。 design 側も `css/balance.css` の
 * `.switch-item[data-color] .sw::after` で同じスコープに閉じている。
 *
 * **export しているのはテストのため。** 影の有無は DOM 階層を辿らないと掴めない
 * 位置にあり (`switch > track > knob`)、 テストから `:scope > span > span` で引くと
 * 見た目を変えないリファクタで壊れる。 スタイル生成は純関数なので、
 * 関数を直接呼んで検証する。
 */
export const toggleDotStyle = (
  isOn: boolean,
  hasSeriesColor: boolean,
): React.CSSProperties => ({
  position: 'absolute',
  top: '2px',
  left: isOn ? '12px' : '2px',
  width: '12px',
  height: '12px',
  borderRadius: '50%',
  background: LightModeSemanticColors.Surface.primary.value, // eclair: surface-primary (#ffffff)
  transition: 'left 0.2s',
  ...(hasSeriesColor
    ? {
        // 全周のソフトな影 + 下方向の drop。 DS thumb と同じ drop shadow の形のまま、
        // トークンより濃くしてある (上記 godoc 参照)。
        boxShadow:
          '0px 0px 2px 0px rgba(0, 0, 0, 0.45), 0px 1px 2px 0px rgba(0, 0, 0, 0.35)',
      }
    : {}),
})

export const ToggleSwitch = ({
  label,
  isOn,
  isDisabled = false,
  onColor,
  onToggle,
}: Props): React.ReactElement => {
  const effectiveOn = isOn && !isDisabled
  // `onColor` を明示指定された = 系列色 switch (design の `[data-color]` 相当)。
  // 既定の accent に落ちる switch は DS どおり輪郭なしのまま。
  const hasSeriesColor = onColor !== undefined
  const trackColor =
    onColor ?? LightModeSemanticColors.Surface.accentPrimary.value
  return (
    <span
      aria-checked={isOn}
      aria-disabled={isDisabled}
      aria-label={label}
      onClick={() => {
        if (!isDisabled) onToggle()
      }}
      onKeyDown={(e) => {
        if (e.key === ' ' || isEnterKey(e)) {
          e.preventDefault()
          if (!isDisabled) onToggle()
        }
      }}
      role="switch"
      style={switchStyle(isDisabled)}
      tabIndex={isDisabled ? -1 : 0}
    >
      <span style={toggleKnobStyle(effectiveOn, trackColor)}>
        <span style={toggleDotStyle(effectiveOn, hasSeriesColor)} />
      </span>
      {label}
    </span>
  )
}
